import type { Coordinate } from '@sembo-private/constraint-builder';
import {
  HotelCodeConstraint,
  PolygonConstraint,
  SelectionPrefix,
  deserializeConstraint,
  serializeConstraint,
} from '@sembo-private/constraint-builder';

import { HotelIngredient } from '@apps/main/modules/recipe/ingredients';

import type {
  AccommodationProductType,
  ExposedActivityIngredient,
  ExposedHotelIngredient,
  OldExposedActivityIngredient,
  OldExposedHotelIngredient,
} from '@libs/trip-planner-interfaces';
import {
  AccommodationRule,
  AccommodationType,
  AmenitiesFilter,
  BoardBasisFilter,
  ExperienceFilter,
  IngredientType,
  InputBoardBasisQL,
  SelectedHotelTypeQL,
} from '@libs/trip-planner-interfaces';
import { toAvailabilityOrder } from '@libs/search-service-smart-search';

import { deserializeConstraintText } from '../ingredientSerializer/legacy/staySerializer/hotelSerializer';

import {
  createActivityIngredient,
  createExposedActivityIngredient,
} from './recipeSerializer.activity.helpers';
import { isAvailabilityOrderQl } from './recipeSerializer.helpers';

export function isOldExposedHotelIngredient(
  exposedHotelIngredient: ExposedHotelIngredient | OldExposedHotelIngredient
): exposedHotelIngredient is OldExposedHotelIngredient {
  return (
    'serializedConstraint' in exposedHotelIngredient ||
    'query' in exposedHotelIngredient ||
    'hearted' in exposedHotelIngredient
  );
}

export function createExposedHotelIngredient(
  hotelIngredient: HotelIngredient
): ExposedHotelIngredient {
  const {
    searchText,
    numberOfNights,
    filters,
    heartedHotels,
    subIngredients,
    polygonId,
  } = hotelIngredient;

  return {
    ingredientType: IngredientType.Hotel,
    destination: searchText,
    polygonId,
    numberOfNights,
    starRating: filters.starRating > 0 ? filters.starRating : 0,
    selectedHotelTypes: filters.selectedHotelTypes.length
      ? filters.selectedHotelTypes
      : undefined,
    freeCancellation: filters.selectedHotelRules.includes(
      AccommodationRule.FreeCancellation
    ),
    boardBasis: filters.boardBasis.length ? filters.boardBasis : undefined,
    hasParking:
      filters.selectedAmenitiesFilters.includes(AmenitiesFilter.Parking) ||
      undefined,
    petsAreAllowed:
      filters.selectedExperienceFilters.includes(
        ExperienceFilter.PetsAllowed
      ) || undefined,
    isBusinessProperty:
      filters.selectedExperienceFilters.includes(ExperienceFilter.Business) ||
      undefined,
    isFamilyProperty:
      filters.selectedExperienceFilters.includes(ExperienceFilter.Family) ||
      undefined,
    hasPool:
      filters.selectedAmenitiesFilters.includes(AmenitiesFilter.Pool) ||
      undefined,
    hasSpa:
      filters.selectedAmenitiesFilters.includes(AmenitiesFilter.Spa) ||
      undefined,
    hasGym:
      filters.selectedAmenitiesFilters.includes(AmenitiesFilter.Gym) ||
      undefined,
    hasInternet:
      filters.selectedAmenitiesFilters.includes(AmenitiesFilter.Wifi) ||
      undefined,
    hasElectricVehicleChargingStation:
      filters.selectedAmenitiesFilters.includes(
        AmenitiesFilter.VehicleChargingStation
      ) || undefined,
    hasAirConditioning:
      filters.selectedAmenitiesFilters.includes(
        AmenitiesFilter.AirConditioned
      ) || undefined,
    heartedHotels: heartedHotels.length ? heartedHotels : undefined,
    subIngredients: subIngredients.length
      ? subIngredients.map(createExposedActivityIngredient)
      : undefined,
  };
}

export function createHotelIngredient(
  exposedHotelIngredient: ExposedHotelIngredient | OldExposedHotelIngredient
): HotelIngredient {
  const { numberOfNights, starRating, subIngredients } = exposedHotelIngredient;

  const hotelIngredient = new HotelIngredient();
  hotelIngredient.numberOfNights = numberOfNights;
  hotelIngredient.filters.starRating = starRating ?? 0;
  hotelIngredient.subIngredients =
    subIngredients?.map(
      (
        subIngredient: ExposedActivityIngredient | OldExposedActivityIngredient
      ) => createActivityIngredient(subIngredient, hotelIngredient.ingredientId)
    ) ?? [];

  const {
    serializedConstraint,
    query,
    apartments,
    hotels,
    housesAndCottages,
    parking,
    petsAllowed,
    pool,
    hearted,
  } = exposedHotelIngredient as OldExposedHotelIngredient;
  const {
    destination,
    hotelCodes,
    polygon,
    polygonId,
    selectedHotelTypes,
    hasParking,
    petsAreAllowed,
    isBusinessProperty,
    isFamilyProperty,
    hasPool,
    hasSpa,
    hasGym,
    hasAirConditioning,
    hasElectricVehicleChargingStation,
    hasInternet,
    heartedHotels,
    boardBasis,
    freeCancellation,
    suggestedBy,
  } = exposedHotelIngredient as ExposedHotelIngredient;

  const backwardsCompatibleHeartedHotels = hearted ?? heartedHotels;

  const searchText = getSearchText({
    serializedConstraint,
    query,
    destination,
    hotelCodes:
      hotelCodes ??
      backwardsCompatibleHeartedHotels?.map(
        (heartedHotel) => heartedHotel.code
      ),
    polygon,
    polygonId,
  });
  hotelIngredient.setSearchText(searchText, null);

  const displayText = getDisplayText({ destination, query, polygonId });
  if (displayText) {
    hotelIngredient.displayText = displayText;
  }

  if (polygon) {
    hotelIngredient.polygonConstraint = new PolygonConstraint(polygon);
  }

  if (polygonId) {
    hotelIngredient.polygonId = polygonId;
  }

  hotelIngredient.filters.selectedHotelTypes = selectedHotelTypes
    ? getNewSelectedHotelTypes(selectedHotelTypes)
    : getOldSelectedHotelTypes(hotels, apartments, housesAndCottages);

  hotelIngredient.filters.selectedAmenitiesFilters =
    getSelectedAmenitiesFilters(
      parking ?? hasParking,
      pool ?? hasPool,
      hasSpa,
      hasGym,
      hasInternet,
      hasAirConditioning,
      hasElectricVehicleChargingStation
    );

  hotelIngredient.filters.selectedExperienceFilters =
    getSelectedExperienceFilters(
      isBusinessProperty,
      petsAllowed ?? petsAreAllowed,
      isFamilyProperty
    );

  hotelIngredient.heartedHotels = backwardsCompatibleHeartedHotels ?? [];

  hotelIngredient.filters.selectedHotelRules = [];
  if (freeCancellation) {
    hotelIngredient.filters.selectedHotelRules.push(
      AccommodationRule.FreeCancellation
    );
  }

  hotelIngredient.filters.boardBasis = boardBasis
    ? getBoardBasisFilters(boardBasis)
    : [];

  if (suggestedBy) {
    hotelIngredient.availabilityOrder = isAvailabilityOrderQl(suggestedBy)
      ? toAvailabilityOrder(suggestedBy)
      : suggestedBy;
  }

  return hotelIngredient;
}

function getSearchText(backwardsCompatibleSearchTexts: {
  serializedConstraint?: string;
  query?: string;
  destination?: string;
  hotelCodes?: string[];
  polygon?: Coordinate[];
  polygonId?: number;
}): string {
  const {
    serializedConstraint,
    query,
    destination,
    hotelCodes,
    polygon,
    polygonId,
  } = backwardsCompatibleSearchTexts;

  if (serializedConstraint) {
    const constraint = deserializeConstraint(serializedConstraint);
    constraint.maxNumberOfHotels = undefined;

    return serializeConstraint(constraint);
  }

  if (query) {
    return deserializeConstraintText(query).searchText;
  }

  if (destination) {
    return deserializeConstraintText(destination).searchText;
  }

  if (hotelCodes?.length) {
    const hotelCodeConstraint = new HotelCodeConstraint(...hotelCodes);

    return `${
      hotelCodeConstraint.prefix
    }:${hotelCodeConstraint.serializeValue()}`;
  }

  if (polygon) {
    const polygonConstraint = new PolygonConstraint(polygon);

    return `${polygonConstraint.prefix}:${polygonConstraint.serializeValue()}`;
  }

  if (polygonId) {
    return '';
  }

  throw new Error('No valid searchText found');
}

function getDisplayText(backwardsCompatibleSearchTexts: {
  query?: string;
  destination?: string;
  polygonId?: number;
}): string | undefined {
  const { query, destination, polygonId } = backwardsCompatibleSearchTexts;

  if (query) {
    return deserializeConstraintText(query).displayText;
  }

  if (destination) {
    return deserializeConstraintText(destination).displayText;
  }

  if (polygonId) {
    return `${SelectionPrefix.PolygonId}: ${polygonId}`;
  }
}

function getNewSelectedHotelTypes(
  selectedHotelTypes: (AccommodationType | SelectedHotelTypeQL)[]
): AccommodationProductType[] {
  const result: AccommodationProductType[] = [];

  if (
    selectedHotelTypes.includes(AccommodationType.Hotel) ||
    selectedHotelTypes.includes(SelectedHotelTypeQL.Hotel)
  ) {
    result.push(AccommodationType.Hotel);
  }

  if (
    selectedHotelTypes.includes(AccommodationType.Apartment) ||
    selectedHotelTypes.includes(SelectedHotelTypeQL.Apartment)
  ) {
    result.push(AccommodationType.Apartment);
  }

  if (
    selectedHotelTypes.includes(AccommodationType.HouseOrCottage) ||
    selectedHotelTypes.includes(SelectedHotelTypeQL.HouseOrCottage)
  ) {
    result.push(AccommodationType.HouseOrCottage);
  }

  return result;
}

function getOldSelectedHotelTypes(
  hotels?: boolean,
  apartments?: boolean,
  housesAndCottages?: boolean
): AccommodationProductType[] {
  const selectedHotelTypes: AccommodationProductType[] = [];

  if (hotels) {
    selectedHotelTypes.push(AccommodationType.Hotel);
  }
  if (apartments) {
    selectedHotelTypes.push(AccommodationType.Apartment);
  }
  if (housesAndCottages) {
    selectedHotelTypes.push(AccommodationType.HouseOrCottage);
  }

  return selectedHotelTypes;
}

function getSelectedAmenitiesFilters(
  hasParking?: boolean,
  hasPool?: boolean,
  hasSpa?: boolean,
  hasGym?: boolean,
  hasInternet?: boolean,
  hasAirConditioning?: boolean,
  hasElectricVehicleChargingStation?: boolean
): AmenitiesFilter[] {
  const selectedAmenitiesFilters: AmenitiesFilter[] = [];

  if (hasParking) {
    selectedAmenitiesFilters.push(AmenitiesFilter.Parking);
  }
  if (hasPool) {
    selectedAmenitiesFilters.push(AmenitiesFilter.Pool);
  }
  if (hasSpa) {
    selectedAmenitiesFilters.push(AmenitiesFilter.Spa);
  }
  if (hasGym) {
    selectedAmenitiesFilters.push(AmenitiesFilter.Gym);
  }
  if (hasInternet) {
    selectedAmenitiesFilters.push(AmenitiesFilter.Wifi);
  }
  if (hasAirConditioning) {
    selectedAmenitiesFilters.push(AmenitiesFilter.AirConditioned);
  }
  if (hasElectricVehicleChargingStation) {
    selectedAmenitiesFilters.push(AmenitiesFilter.VehicleChargingStation);
  }

  return selectedAmenitiesFilters;
}

function getSelectedExperienceFilters(
  isBusinessProperty?: boolean,
  petsAreAllowed?: boolean,
  isFamilyProperty?: boolean
): ExperienceFilter[] {
  const selectedExperienceFilters: ExperienceFilter[] = [];
  if (petsAreAllowed) {
    selectedExperienceFilters.push(ExperienceFilter.PetsAllowed);
  }
  if (isBusinessProperty) {
    selectedExperienceFilters.push(ExperienceFilter.Business);
  }
  if (isFamilyProperty) {
    selectedExperienceFilters.push(ExperienceFilter.Family);
  }

  return selectedExperienceFilters;
}

function getBoardBasisFilters(
  boardBasis: (BoardBasisFilter | InputBoardBasisQL)[]
): BoardBasisFilter[] {
  const result: BoardBasisFilter[] = [];

  if (
    boardBasis.includes(BoardBasisFilter.AllInclusive) ||
    boardBasis.includes(InputBoardBasisQL.AllInclusive)
  ) {
    result.push(BoardBasisFilter.AllInclusive);
  }

  if (
    boardBasis.includes(BoardBasisFilter.Breakfast) ||
    boardBasis.includes(InputBoardBasisQL.Breakfast)
  ) {
    result.push(BoardBasisFilter.Breakfast);
  }

  if (
    boardBasis.includes(BoardBasisFilter.FullBoard) ||
    boardBasis.includes(InputBoardBasisQL.FullBoard)
  ) {
    result.push(BoardBasisFilter.FullBoard);
  }

  if (
    boardBasis.includes(BoardBasisFilter.HalfBoard) ||
    boardBasis.includes(InputBoardBasisQL.HalfBoard)
  ) {
    result.push(BoardBasisFilter.HalfBoard);
  }

  if (
    boardBasis.includes(BoardBasisFilter.RoomOnly) ||
    boardBasis.includes(InputBoardBasisQL.RoomOnly)
  ) {
    result.push(BoardBasisFilter.RoomOnly);
  }
  return result;
}
